import { useState } from 'react'
import * as React from 'react'
import { Head, router } from '@inertiajs/react'
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import DashboardLayout from '@/components/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import WorkflowCard from '@/components/WorkflowCard'
import WorkflowModal from '@/components/WorkflowModal'
import { Plus } from 'lucide-react'

interface Workflow {
  id: number
  name: string
  description: string | null
  order: number
  isActive: boolean
  stopProcessingOnMatch: boolean
  stopProcessingOnFail: boolean
  filtersCount: number
  actionsCount: number
  stats: {
    emailsProcessed: number
    emailsMatched: number
    emailsFailed: number
    matchRate: number
    failRate: number
    lastProcessedAt: string | null
  }
}

interface IndexProps {
  account: {
    id: number
    name: string
  }
  workflows: Workflow[]
  currentUser?: {
    name: string
    email: string
    avatarUrl?: string
  }
  gmailConnection: {
    id: number
    email: string
    syncStatus: string
    active: boolean
  } | null
}

function SortableWorkflowCard({
  workflow,
  accountId,
  onEdit,
  onDelete,
}: {
  workflow: Workflow
  accountId: number
  onEdit: (id: number) => void
  onDelete?: (id: number) => void
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: workflow.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div ref={setNodeRef} style={style}>
      <WorkflowCard
        workflow={workflow}
        accountId={accountId}
        onEdit={onEdit}
        onDelete={onDelete}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  )
}

export default function Index({
  account,
  workflows,
  currentUser,
  gmailConnection,
}: IndexProps) {
  const [workflowsList, setWorkflowsList] = useState(workflows)
  const [showModal, setShowModal] = useState(false)
  const [editingWorkflowId, setEditingWorkflowId] = useState<number | null>(null)

  // Sync local state with prop when workflows update from server
  React.useEffect(() => {
    setWorkflowsList(workflows)
  }, [workflows])

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      setWorkflowsList((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id)
        const newIndex = items.findIndex((item) => item.id === over.id)
        const newItems = arrayMove(items, oldIndex, newIndex)

        // Update order values
        const reorderedWorkflows = newItems.map((item, index) => ({
          ...item,
          order: index + 1,
        }))

        // Send reorder request to server using fetch (not Inertia) to avoid blank modal
        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')

        fetch(`/accounts/${account.id}/workflows/reorder`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken || '',
          },
          body: JSON.stringify({
            workflows: reorderedWorkflows.map((wf) => ({
              id: wf.id,
              order: wf.order,
            })),
          }),
        }).catch((error) => {
          console.error('Failed to reorder workflows:', error)
          // Reload on error to restore correct order
          router.reload({ only: ['workflows'] })
        })

        return reorderedWorkflows
      })
    }
  }

  const handleEdit = (id: number) => {
    setEditingWorkflowId(id)
    setShowModal(true)
  }

  const handleDelete = (id: number) => {
    // Optimistically remove from list and recalculate order numbers
    setWorkflowsList((items) => {
      const filtered = items.filter((item) => item.id !== id)
      // Recalculate order numbers to be sequential
      return filtered.map((item, index) => ({
        ...item,
        order: index + 1,
      }))
    })
  }

  const handleCloseModal = () => {
    setShowModal(false)
    setEditingWorkflowId(null)
    router.reload({ only: ['workflows'] })
  }

  const editingWorkflow = editingWorkflowId
    ? workflowsList.find((w) => w.id === editingWorkflowId)
    : null

  return (
    <>
      <Head title="Workflows" />
      <DashboardLayout
        currentUser={currentUser}
        gmailConnection={gmailConnection}
        account={account}
        activeTab="workflows"
      >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-blue-950 dark:text-white mb-2">
              Workflows
            </h1>
          </div>
          <Button onClick={() => setShowModal(true)} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            New Workflow
          </Button>
        </div>

        {/* Workflows List */}
        {workflowsList.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={workflowsList.map((w) => w.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-4">
                {workflowsList.map((workflow) => (
                  <SortableWorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    accountId={account.id}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                No workflows yet. Create your first workflow to get started.
              </p>
              <Button onClick={() => setShowModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Workflow
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Create/Edit Modal */}
        {showModal && (
          <WorkflowModal
            accountId={account.id}
            workflow={editingWorkflow}
            onClose={handleCloseModal}
          />
        )}
      </div>
    </DashboardLayout>
    </>
  )
}
